import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MATTR_SMS_NUMBER, SMS_LINK, getApiBaseUrl } from '../../config/api';
import RacePlanCta from './RacePlanCta';
import '../../css/JokePage.css';

/**
 * The QR-code joke page (mattr.coach/joke).
 *
 * Reached by scanning the QR on a running singlet, mid-run, in the park.
 * Design constraints that drive everything here:
 *   - ~3 seconds of attention, read at arm's length, often in direct sunlight
 *   - The shirt is IMMUTABLE once printed. This page is the mutable half, so
 *     every changeable thing (joke, number, copy) lives here, never on the shirt.
 *
 * This route is imported EAGERLY in index.js (unlike every other route, which is
 * lazy). A QR scan is always a cold direct load, so a lazy chunk would cost a
 * second round trip on park LTE and open the page on the "Loading..." fallback.
 * ~2KB against a ~190KB main.js is worth the RTT.
 *
 * ── WHAT TO ENCODE IN THE QR ────────────────────────────────────────────────
 * Tag every print run so scans are attributable to a garment, not lumped in with
 * anyone who typed the URL:
 *
 *     https://mattr.coach/joke?src=singlet     <- the original shirt
 *     https://mattr.coach/joke?src=runclub     <- a run-club giveaway batch
 *     https://mattr.coach/joke?src=corral      <- race-day / expo
 *
 * `src` is ANALYTICS-ONLY and must never reach the sms: body — CTA_KEYWORDS
 * matches "JOKE" exactly (sms_service.py), so a "JOKE-RUNCLUB" body would kill
 * signup attribution outright.
 *
 * It reaches two places: every GA4 event on this page (blocked for some of this
 * all-mobile audience), and utm_campaign on the learn-more link, which persists
 * to the landing_page_visits table (`joke-runclub`) and survives ad blockers.
 *
 * The FIRST shirt's QR carries no `src`, so its scans arrive untagged and are
 * indistinguishable from typed traffic. That is unfixable without a reprint —
 * the shirt is the immutable half. Tag the next run.
 */

// The joke is on Matthew, never on the reader. They just gave us their attention
// mid-run; a punchline at their expense is a bad way to ask them for a signup.
// Self-deprecation is also the strongest hook framework in our own post-history.
// Changing these is a one-line edit + a ~60s Vercel deploy. No reprint. That is
// the whole point of keeping the joke off the shirt.
//
// One is picked at RANDOM per page load (see `pickJoke`). Two reasons the set
// beats a single joke: the shirt gets scanned repeatedly by the same run club, so
// a fixed joke goes stale on the exact audience most likely to convert; and
// `joke_id` already rides every GA4 event on this page, so a rotating set turns
// the funnel into a free read on WHICH joke earns reveals and CTA taps.
//
// The set is NEW YORK specific on purpose. Every person who can reach this page
// scanned a QR off a singlet in a NYC park, so an insider reference (First Ave,
// Harlem Hill, 9+1, the Queensboro) does work a generic running joke cannot: it
// proves the person in the shirt runs the same roads they do. Recognition is the
// cheapest trust we can buy in three seconds.
//
// Most punchlines also land on a problem a COACH solves — going out too fast, no
// structure, easy days run hard. That is deliberate. The old set was funny but
// unrelated to coaching, which made "Want an AI running coach?" a hard cut right
// after the payoff. When the joke names the pain, the ask reads as the next beat
// instead of a swerve.
//
// Rules for adding one:
//   - Q&A shape, punchline at Matthew's expense, never the reader's.
//   - Short enough to read at arm's length, in sunlight, while moving. Keep the
//     punchline near/under ~60 characters; .joke-setup is clamped to 3rem and a
//     long setup pushes the reveal button off a small viewport.
//   - Unique `id` — it is the analytics key, so reusing one silently merges two
//     jokes' funnels.
//   - An insider reference must be readable by a tourist too. "First Avenue told
//     me I was fast" still lands if you have never run it; a joke that dies
//     without local knowledge costs us the visitor half of the park.
export const JOKES = [
  // The NYC Marathon's classic blow-up: the First Ave crowd is so loud that
  // everyone surges at mile 16 and pays for it by 18. Names the pacing problem.
  {
    id: 'first-ave-v1',
    setup: 'Why did I blow up at mile 18?',
    punchline: 'First Avenue told me I was fast and I believed it.',
  },
  // Every Manhattan runner's actual workout structure. Names the no-plan problem.
  {
    id: 'lights-v1',
    setup: 'Who writes my interval workouts?',
    punchline: 'The crosstown lights on 14th Street.',
  },
  // Central Park insider, and the pain is running easy days too hard — the exact
  // thing the easy-pace anchor exists to fix. Founder call (2026-08-10): the
  // Central Park reference reads fine to this audience, so the sharper original
  // stands. The softened rewrite that briefly replaced it was cut as a near
  // duplicate of this premise, not because it was wrong.
  {
    id: 'harlem-hill-v1',
    setup: 'Why does my easy run set off my heart rate alarm?',
    punchline: "Harlem Hill doesn't care what I named the workout.",
  },
  // NYRR's 9+1 (nine races plus a volunteer shift = guaranteed marathon entry).
  // No product bridge; this one is pure in-group recognition, and it is the joke
  // most likely to make a local feel seen.
  {
    id: 'nine-plus-one-v1',
    setup: "Why did I sign up for nine races I don't want to run?",
    punchline: "The tenth one is the marathon. That's how they get you.",
  },
  // Replaced the old queensboro-v1, which required marathon experience to read as
  // a joke at all rather than a statement. Sprinting for a train you did not need
  // to catch is universal, needs zero running vocabulary, and still names a
  // coaching idea (goal pace) in passing.
  {
    id: 'train-v1',
    setup: 'When do I actually hit my goal pace?',
    punchline: "Sprinting for a train I could've waited four minutes for.",
  },
  // Lightest of the set. The bodega bacon-egg-and-cheese is the city's default
  // breakfast, so this reads as local without requiring a race résumé.
  {
    id: 'bec-v1',
    setup: "What's my recovery protocol?",
    punchline: 'A bacon egg and cheese, and denial.',
  },
  // The West Side Highway is the city's thirst-trap running path — the dress code
  // is essentially none. So the weird thing is not the QR code, it is being
  // CLOTHED there at all, which is why the punchline must land on "a shirt at
  // all" and not on "this shirt." An earlier draft opened with "Not the QR code"
  // and pointed the joke at the wrong object: the QR is the misdirection the
  // setup plants, never the answer. Do not put it back.
  {
    id: 'west-side-v1',
    setup: "What's the weirdest part of this shirt?",
    punchline: "That I'm wearing a shirt at all on the West Side Highway.",
  },
  // KEPT from the original set, and the only non-NYC one. Like west-side-v1 it
  // explains the shirt the reader is standing in front of, which is worth a slot:
  // it acknowledges the setup instead of ignoring it.
  {
    id: 'splits-v1',
    setup: 'Why did I put a QR code on my shirt?',
    punchline: "My splits weren't going to be the thing that impressed you.",
  },
];

// Chicago Marathon weekend (race day Sun 2026-10-11). The singlet's QR is the
// untagged original, so the page cannot tell a Lincoln Park scan from a Central
// Park one by URL. The date is the only signal, and on race weekend the shirt is
// in Chicago. Inside this window a NYC set would prove the opposite of what the
// jokes are for: that the person in the shirt does NOT run these roads.
//
// Same rules as JOKES. Fresh `chi-` ids so the race-weekend funnel reads apart
// from the NYC one in GA4; splits-v1 is city-agnostic, so it rides along under
// its existing id.
export const CHICAGO_JOKES = [
  // The one hill on a flat course, at the worst possible moment. Every Chicago
  // finisher knows it. Names the nothing-left-at-the-end pacing problem.
  {
    id: 'chi-roosevelt-v1',
    setup: "What's the only hill in Chicago?",
    punchline: 'Roosevelt Road, at mile 26, when I have nothing left.',
  },
  // GPS goes haywire between the Loop's towers; watches report absurd splits and
  // runners chase them. Names the going-out-too-fast problem.
  {
    id: 'chi-gps-v1',
    setup: 'Why did my watch say I ran a 5:40 mile downtown?',
    punchline: 'The skyscrapers lied. I believed them.',
  },
  // "Flat and fast" is the course's whole reputation, and the reason half the
  // field goes out at a pace they never trained. Names the pacing problem.
  {
    id: 'chi-flat-fast-v1',
    setup: 'Why did I go out too fast?',
    punchline: "Everyone said 'flat and fast.' I only heard 'fast.'",
  },
  // The halfway crowds are Chicago's First Avenue: everyone surges and pays for
  // it in the 20s. Same structure as first-ave-v1, on purpose.
  {
    id: 'chi-halfway-v1',
    setup: 'Why did I blow up at mile 22?',
    punchline: 'Mile 13 cheered for me and I believed it.',
  },
  // The final northbound stretch to Roosevelt. Names the late-race fade.
  {
    id: 'chi-michigan-v1',
    setup: "What's the longest street in Chicago?",
    punchline: 'Michigan Avenue, from mile 24 to 26.',
  },
  // Race-morning logistics in Grant Park. Names the no-race-day-plan problem.
  {
    id: 'chi-corral-v1',
    setup: 'How did I warm up for Chicago?',
    punchline: 'Ninety minutes in a Grant Park porta-potty line.',
  },
  JOKES.find((j) => j.id === 'splits-v1'),
];

// Thu 10/8 00:00 through Wed 10/14 23:59 Central (CDT = UTC-5). Opens before the
// expo and shakeout, and stays open through the post-race days when people find
// the tab still open on their phone and finally act. Afterwards the page falls
// back to the NYC set on its own: no deploy needed to undo it.
export const CHICAGO_WINDOW = {
  start: Date.UTC(2026, 9, 8, 5),
  end: Date.UTC(2026, 9, 15, 5),
};

/** The joke set for this moment. Reads Date.now() so tests can pin the clock. */
export const activeJokes = (now = Date.now()) =>
  now >= CHICAGO_WINDOW.start && now < CHICAGO_WINDOW.end ? CHICAGO_JOKES : JOKES;

// Race-weekend CTA (RacePlanCta): the free race plan through the end of race day, then just
// the signup for the post-race days still inside the window. Outside the window the page keeps
// its original sms: JOKE CTA, byte for byte.
export const CHICAGO_RACE_END = Date.UTC(2026, 9, 12, 5); // Sun 10/11 23:59 Central

export const raceCtaFor = (now = Date.now()) =>
  activeJokes(now) === CHICAGO_JOKES
    ? { race: 'Chicago Marathon', label: 'Chicago', raceDay: 'Sunday', showPlan: now < CHICAGO_RACE_END }
    : null;

/** One joke per page load. Held in state so a re-render can never swap the
 *  punchline out from under the setup it belongs to. */
const pickJoke = () => {
  const set = activeJokes();
  return set[Math.floor(Math.random() * set.length)];
};

// `?&body=` is the cross-platform form: iOS wants `&body=`, Android wants `?body=`.
//
// NOTE: config/api.js warns against prefilling a body, but that warning is scoped
// to the FORM flow, where the welcome text has already asked a question and a
// prefilled body would become the user's (wrong) first answer. This flow is the
// opposite: the stranger initiates, nothing is pending, and the onboarding service
// returns the consent disclosure WITHOUT processing the body
// (sms_onboarding_service.py:247-249). The body is what fires `joke_signup_keyword`
// attribution in sms_service.py CTA_KEYWORDS, so do not remove it.
const JOKE_SMS_LINK = `${SMS_LINK}?&body=JOKE`;

/** +19179949725 -> (917) 994-9725. Derived so the number has one source of truth. */
const formatSmsNumber = (e164) => {
  const digits = (e164 || '').replace(/\D/g, '').replace(/^1/, '');
  if (digits.length !== 10) return e164;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const track = (event, params) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event, params);
  }
};

function JokePage() {
  const [revealed, setRevealed] = useState(false);
  // Lazy initializer, NOT pickJoke() — an inline call would re-roll the joke on
  // every render (the stats fetch alone causes one), so the reader could watch
  // the setup change under their thumb.
  const [joke] = useState(pickJoke);
  const [raceCta] = useState(() => raceCtaFor());
  const [stats, setStats] = useState({
    athletes: null,
    workouts: null,
    donationTotal: null,
    donationGoal: null,
  });
  const punchlineRef = useRef(null);

  // ?src= tags which singlet / run-club a scan came from. ANALYTICS-ONLY: it rides
  // GA4 events, NEVER the sms: body — CTA_KEYWORDS matches JOKE exactly (sms_service
  // .py), so a "JOKE-RUNCLUB" body would kill attribution. undefined when absent.
  const src = new URLSearchParams(window.location.search).get('src') || undefined;

  // Funnel denominator: a mount event so reveal-rate = joke_punchline_reveal /
  // joke_view is unambiguous (crisper than filtering generic GA4 page_views by path).
  useEffect(() => {
    track('joke_view', { joke_id: joke.id, src });
  }, [src, joke.id]);

  // Social proof + Achilles fundraiser progress, shown at the DECISION moment
  // (revealed block only). ONE request for both: this endpoint already returns the
  // donation figures alongside the counts (client_routes.py get_landing_page_stats),
  // so the ticker is free — no second round trip on park LTE. Async + graceful: the
  // joke's first paint never waits on this, and a slow/failed fetch simply shows no
  // stat and no ticker — it can never block or break the page.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${getApiBaseUrl()}/api/clients/landing-page/stats`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        setStats({
          athletes: d.athlete_count ?? null,
          workouts: d.workouts_completed ?? null,
          donationTotal: d.donation_total ?? null,
          donationGoal: d.donation_goal ?? null
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Move focus to the punchline once it exists. The reveal button unmounts, so
  // without this, focus falls to <body> and the screen reader cursor jumps back
  // to the top of the page: a blind runner taps, hears nothing, and loses their
  // place. Focusing the payoff also puts the CTA next in reading order.
  useEffect(() => {
    if (revealed && punchlineRef.current) {
      punchlineRef.current.focus();
    }
  }, [revealed]);

  // The global body rule (style.css) is display:flex + center + height:100vh, which
  // centers this whole page and clips its top off-screen once the punchline reveal
  // makes the content taller than the viewport. Neutralize it while mounted so the
  // page grows and scrolls from the top; the short unrevealed state still centers via
  // .joke-page's own rule. Cleanup restores the body for other routes.
  useEffect(() => {
    document.body.classList.add('joke-active');
    return () => document.body.classList.remove('joke-active');
  }, []);

  const handleReveal = () => {
    setRevealed(true);
    track('joke_punchline_reveal', { joke_id: joke.id, src });
  };

  const handleCtaTap = () => {
    track('joke_cta_tap', { joke_id: joke.id, src });
  };

  // The evaluate-first path at the decision moment. Distinct from
  // joke_wordmark_tap (the footer mark) on purpose: this one is the promoted link
  // and is the measurable proxy for the shirt's BRAND-AWARENESS job, which the
  // text-only funnel cannot show. Carries joke_id, so a joke that drives reading
  // rather than texting is visible rather than looking like a flat failure.
  const handleLearnMoreTap = () => {
    track('joke_learnmore_tap', { joke_id: joke.id, src });
  };

  // Carry the singlet tag THROUGH to the landing page, durably. `src` rides the
  // GA4 events, but GA4 is partially blocked on this all-mobile audience, so a
  // shirt variant would be invisible exactly when it mattered. utm_campaign is one
  // of the three keys LandingPage forwards to /clients/landing-page/visit, which
  // persists it — so `?src=runclub` becomes a queryable `joke-runclub` row.
  // utm_content would have been the semantically nicer home for it, but the
  // backend stores only source/medium/campaign and drops anything else.
  //
  // SANITIZED, not trusted: `src` is an attacker-controllable query param that
  // ends up in a String(100) DB column. A slug charset keeps junk (and anything
  // exotic) out of the campaign field, and the length cap keeps it well inside the
  // column. Empty/absent falls back to the plain 'joke' campaign.
  const srcSlug = (src || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 24);
  const learnMoreHref =
    '/?utm_source=shirt&utm_medium=qr&utm_campaign=' +
    (srcSlug ? `joke-${srcSlug}` : 'joke');

  // The ticker needs BOTH figures and a positive goal, or the bar is meaningless.
  const hasTicker =
    stats.donationTotal != null && stats.donationGoal != null && stats.donationGoal > 0;
  const donationPct = hasTicker
    ? Math.max(0, Math.min(100, (stats.donationTotal / stats.donationGoal) * 100))
    : 0;
  // HONESTY GATE, not a style choice. The backend only routes a first charge to
  // Achilles while the campaign is UNDER goal (stripe_service.apply_invoice_paid
  // requires donation_total_usd < DONATION_GOAL_USD). Once the goal is met the
  // promise stops being true, so the claim must disappear on its own rather than
  // wait for someone to remember to delete it. The ticker itself stays — a funded
  // goal is the good news — but it stops promising the reader's money moves.
  const donationClaimTrue = hasTicker && stats.donationTotal < stats.donationGoal;

  // Tracked separately on purpose: this is the "evaluate first" path. If
  // wordmark taps outrun CTA taps, serious runners are choosing to research
  // rather than text a stranger, which means the ASK is wrong for them, not
  // the joke. That is a signal no other event on this page can give.
  const handleWordmarkTap = () => {
    track('joke_wordmark_tap', { joke_id: joke.id, src });
  };

  return (
    <main className="joke-page">
      <div className="joke-card">
        {/* h1, not p: the setup is the page's heading, so heading navigation
            finds something. Styling stays on the class. */}
        <h1 className="joke-setup">{joke.setup}</h1>

        {/* This wrapper is ALWAYS mounted, which is the whole point. An
            aria-live region must exist in the DOM BEFORE its content changes;
            a node inserted already-populated is not reliably announced by
            VoiceOver/NVDA/JAWS. The previous version put aria-live on the
            punchline itself, so it announced nothing. */}
        <div className="joke-reveal-slot" aria-live="polite">
          {!revealed ? (
            <button
              type="button"
              className="joke-reveal-btn"
              onClick={handleReveal}
            >
              Tap for the answer
            </button>
          ) : (
            <p className="joke-punchline" ref={punchlineRef} tabIndex={-1}>
              {joke.punchline}
            </p>
          )}
        </div>
      </div>

      {revealed && (
        <div className="joke-cta">
          {raceCta ? (
            <RacePlanCta
              race={raceCta.race}
              raceLabel={raceCta.label}
              raceDay={raceCta.raceDay}
              showPlan={raceCta.showPlan}
              learnMoreHref={learnMoreHref}
              onLearnMore={handleLearnMoreTap}
              src={src}
              utmCampaign={srcSlug ? `joke-${srcSlug}-chicago` : 'joke-chicago'}
              donationLine={
                donationClaimTrue
                  ? `Your first month funds my Achilles fundraiser, not my shoe budget ($${stats.donationTotal.toLocaleString()} of $${stats.donationGoal.toLocaleString()} raised).`
                  : null
              }
            />
          ) : (
          <>
          {/* "AI" is deliberate and load-bearing, not a caveat. It sets honest
              expectations BEFORE the text (the consent SMS discloses AI anyway,
              so hiding it here just delays a mild bait-and-switch), and it turns
              the AI into the reason to text: an AI coach answers every time,
              instantly, unlike a human. This does NOT contradict the "joke beats
              AI as a hook" lesson: the joke is still the hook; by the CTA the
              reader has laughed and is deciding, and "AI" is decision info, not
              the pitch. "helps you chase a PR" stays human and goal-framed. */}
          <p className="joke-cta-question">
            Want an AI running coach that helps you chase a PR and actually makes
            you laugh?
          </p>

          {/* Social proof at the decision moment. Renders only if the async stats
              fetch returned (never blocks the joke's first paint; absent on failure). */}
          {(stats.athletes != null || stats.workouts != null) && (
            <div className="joke-stats">
              {stats.athletes != null && (
                <div className="joke-stat">
                  <span className="joke-stat-num" aria-hidden="true">
                    {stats.athletes.toLocaleString()}
                  </span>
                  <span className="joke-stat-label" aria-hidden="true">
                    runners training
                  </span>
                  <span className="sr-only">
                    {stats.athletes.toLocaleString()} runners training with Mattr
                  </span>
                </div>
              )}
              {stats.athletes != null && stats.workouts != null && (
                <span className="joke-stat-divider" aria-hidden="true" />
              )}
              {stats.workouts != null && (
                <div className="joke-stat">
                  <span className="joke-stat-num" aria-hidden="true">
                    {stats.workouts.toLocaleString()}
                  </span>
                  <span className="joke-stat-label" aria-hidden="true">
                    workouts logged
                  </span>
                  <span className="sr-only">
                    {stats.workouts.toLocaleString()} workouts logged
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Achilles fundraiser, sitting between the social proof and the button
              because it is DECISION information: signing up is also a donation, and
              the reader should know that before they commit, not after.

              Copy rules, all load-bearing:
              - "first month" NEVER "$15 donation". This is a recurring subscription
                with a 14-day trial; calling it a one-time donation would misstate
                an auto-renewing charge, and it drags the price up to the decision
                moment where /chicago deliberately removed it.
              - It is TRUE for this page's cohort: create_checkout_session defaults
                donation_cohort=True (stripe_service.py) and the checkout route does
                not override it, so a JOKE-attributed signup's first real post-trial
                charge is stamped to Achilles like any other. Do not weaken this to
                "may go" — it goes.
              - No competing "donate directly" link, unlike /chicago. That link
                exists there because a personal-IG audience skews non-runner; every
                single person who can reach THIS page scanned a QR off a singlet
                mid-run, so a donate path here only cannibalizes the signup.

              Renders only when the fetch returned both figures; a miss degrades to
              nothing, exactly like the stats above. */}
          {hasTicker && (
            <div className="joke-donation">
              {donationClaimTrue && (
                <p className="joke-donation-claim">
                  Your first month funds my Achilles fundraiser, not my shoe budget.
                </p>
              )}
              <div
                className="joke-ticker"
                role="img"
                aria-label={`$${stats.donationTotal.toLocaleString()} of $${stats.donationGoal.toLocaleString()} raised for Achilles International`}
              >
                <div className="joke-ticker-nums" aria-hidden="true">
                  <span className="joke-ticker-amount">
                    ${stats.donationTotal.toLocaleString()}
                  </span>
                  <span className="joke-ticker-goal">
                    of ${stats.donationGoal.toLocaleString()} raised
                  </span>
                </div>
                <div className="joke-ticker-bar" aria-hidden="true">
                  <span
                    className="joke-ticker-fill"
                    style={{ width: `${donationPct}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          <a
            className="joke-cta-btn"
            href={JOKE_SMS_LINK}
            onClick={handleCtaTap}
          >
            {/* Chat-bubble glyph (not a paper-plane, which reads as "submit here";
                not the iOS green, which reads as impersonating Messages). It signals
                "this opens a message" pre-lexically, before the label is read — the
                key to resolving the tap-vs-text confusion. currentColor => inherits
                the button's white so it survives glare as one unit. */}
            <svg className="joke-cta-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
            </svg>
            Meet your coach
          </a>
          {/* Two jobs. (1) The click-to-text drop-off is people opening Messages and
              never hitting send, so name the action. (2) DE-RISK the ask, which is
              the measured problem: 12 of 14 visitors revealed the punchline and only
              2 tapped this button (GA4, wk of 2026-08-03). The blocker is almost
              certainly perceived commitment — a stranger's number, plus a required
              CTIA line right below promising 10-20 texts a week — not missing info.
              Loss aversion dominates at that moment, so removing the sense that this
              is irreversible is worth more than adding another route out.

              "Nothing starts unless you answer yes" is literally true, not a
              softener: texting JOKE creates an SMSOnboardingState at current_step
              ='consent' and sends the disclosure. No coaching content is ever sent
              before an affirmative reply (sms_onboarding_service CONSENT_AFFIRMATIVE),
              and ignoring it leaves them at that step forever.

              Deliberately does NOT copy /chicago's "your coach builds your plan"
              wording, which would misstate the double opt-in on the one line that
              touches it. The old ending, "then your coach takes over", implied the
              coach starts on its own — the opposite of the reassurance needed. */}
          <p className="joke-cta-send">
            Opens a text with JOKE ready to go. <strong>Hit send</strong> and
            we&apos;ll reply with one question. Nothing starts unless you answer yes.
          </p>
          {/* Framed as RECOVERY, not a second ask. The button sells the outcome
              ("meet your coach") and deliberately carries NO number, so this line
              is the SOLE place the number appears (do not re-add it to the button
              or it reads as the same request twice). Do not delete it: sms: is
              inert on desktop, and when body prefill fails this line is the only
              thing that makes someone type JOKE, the sole trigger for attribution. */}
          <p className="joke-cta-fallback">
            Doesn&apos;t open? Text <strong>JOKE</strong> to{' '}
            <strong>{formatSmsNumber(MATTR_SMS_NUMBER)}</strong>
          </p>

          {/* The evaluate-first path, promoted. This destination was ALREADY here —
              the wordmark at the bottom links to the landing page — but it is a small
              muted footer mark and drew 1 tap against 2 CTA taps. So this is not a
              new route, it is an invisible one made visible at the moment of
              decision.

              Why it earns a slot despite the usual rule that a secondary CTA
              cannibalizes the primary: 83% of people who reveal the punchline take
              NO action at all, so the realistic alternative to this link is leaving,
              not texting. A graded ladder (laugh -> read -> text) beats a binary
              (text or go) when the primary ask is high-commitment and the audience
              is strangers.

              Label rules:
              - NOT "Learn more", a famously low-intent label that attracts idle
                clicks and predicts nothing.
              - NAME THE BRAND, BUT ACKNOWLEDGE IT IS UNKNOWN. "See how Mattr works"
                shipped first and was broken: the word "Mattr" appeared for the FIRST
                time in that label. Everything above it says "an AI running coach" and
                never names the thing, so the link asked a stranger to click a brand
                they had never seen. "New to Mattr?" introduces the name and concedes
                they do not know it, which is true of 100% of this audience.
              - "What it does", NOT "how it works". Mechanism is not the open question
                three seconds after a joke; "is this real and what do I get" is.
              - The "New to Mattr?" opener is NOT the same as the "not ready to text?"
                hedge that was rejected earlier. That one framed TEXTING as a big
                commitment and undercut the de-risking line above. This one frames the
                BRAND as unfamiliar, which costs the primary ask nothing.
              - Styled as a LINK, never a button, so it cannot compete visually with
                the primary ask — but it needs separation from the two troubleshooting
                lines above it (hit-send, "doesn't open?") or it reads as a third piece
                of troubleshooting rather than a different path. See the rule in the
                stylesheet.

              UTM params, NOT a bare ?src=joke. LandingPage's mount effect posts
              utm_source/utm_medium/utm_campaign to /clients/landing-page/visit,
              which persists them to the landing_page_visits table — a queryable row
              per shirt-driven visit that survives ad blockers. A `src` param would
              have been read by nobody and lived only in GA4, which this audience
              (100% mobile) partially blocks, and which hides query strings in the
              default Pages report. These three keys are the exact ones the backend
              stores; adding a fourth would be silently dropped.

              This is the only measure of the BRAND-AWARENESS half of what the
              singlet is for — the text-only funnel cannot show someone who scanned,
              laughed, remembered the name, and looked it up later. Tracked in GA4
              separately from joke_wordmark_tap so the promoted link and the footer
              mark never blur together. */}
          {/* A plain <a>, deliberately, NOT a react-router <Link>. <Link> would save
              a document reload (one round trip instead of three on park LTE) and was
              tried — but react-router-dom v7 is unresolvable under CRA's jest: its
              `main` field points at ./dist/main.js, a file the package does not ship,
              and the real build is reachable only through an `exports` map this
              resolver cannot read. Importing it here costs FOUR global workarounds
              (three moduleNameMapper entries plus a TextEncoder polyfill), each one a
              divergence between test and production module resolution, and it takes
              down all 33 tests on this file — including the CTIA disclosure locks
              that protect the 10DLC campaign — until every one is in place.

              Not worth one saved round trip on the way OUT of the page. The footer
              wordmark uses <a> for the same reason. Revisit only if the app moves off
              react-scripts. */}
          <a
            className="joke-learnmore"
            href={learnMoreHref}
            onClick={handleLearnMoreTap}
          >
            New to Mattr? See what it does &rarr;
          </a>
          </>
          )}

          {/* CTIA point-of-CTA disclosures, trimmed to the elements that carry
              real weight for THIS flow. The singlet carries only a QR, so this
              page is the point of call-to-action, and carriers audit the CTA URL
              against the registered 10DLC campaign.

              Deliberately trimmed, and why each cut is defensible here:
              - No "signs you up": texting JOKE does NOT sign anyone up. It
                triggers a consent request; signup happens on YES. Saying
                otherwise was factually false in a compliance disclosure.
              - No "18+ only": the real age gate is the logged attestation in the
                YES reply (sms_onboarding_service.py CONSENT_DISCLOSURE_TEXT). A
                passive line here was never the gate.
              - No sponsor name / email: the wordmark is on the page and HELP is
                the accepted contact channel.
              - This is a DOUBLE opt-in. No content is ever sent before YES, and
                the consent SMS carries the full disclosure set. That message,
                not this line, is where consent is actually captured.

              Rules if you touch this:
              - Must stay INSIDE the revealed block, adjacent to the CTA. Never
                behind an accordion, a "legal" link, or another tap.
              - The frequency here MUST match CONSENT_DISCLOSURE_TEXT in
                backend/services/sms_onboarding_service.py. If they drift, an
                auditor reads it as an unmanaged program. */}
          <p className="joke-legal">
            Coaching texts. Msg frequency varies, usually a text or two on training
            days. Msg &amp; data rates may apply. Reply STOP to cancel, HELP for
            help.{' '}
            <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a>
          </p>
        </div>
      )}

      {/* A link, not inert text. The only people who can reach this page are
          running at the founder's pace, i.e. serious runners, and serious
          runners EVALUATE before committing (they already have a plan or a
          coach). Texting a stranger's number from a singlet is the only path
          otherwise. This is not "adding content" — the wordmark was already
          here; it just went nowhere. The proof lives on the landing page, so
          link to it rather than duplicating it here. */}
      <a className="joke-wordmark" href="/" onClick={handleWordmarkTap}>
        mattr.coach
      </a>
    </main>
  );
}

export default JokePage;
